import React from 'react'
import { Text, RadioButton, Checkbox } from '@sketch/components'
import { ShareWithoutVersion } from 'modules/versioning'
import usePublicPermissions from 'modules/shares/modals/ShareSettingsModal/PublicPermissions/usePublicPermissions'
import { usePublicInlineModal } from 'modules/shares/modals/ShareSettingsModal/PublicPermissions'
// Usage of PublicAccessLevel and PrivateAccessLevel will not break the build
// eslint-disable-next-line no-restricted-imports
import { PublicAccessLevel } from '@sketch/gql-types/expansive'

export const EveryoneTitle = ({ isPublic }: { isPublic: boolean }) => (
  <>Everyone ({!isPublic ? 'No Access' : 'Public'})</>
)

export const EveryonePanel = ({ share }: { share: ShareWithoutVersion }) => {
  const [modal, openModal] = usePublicInlineModal(
    share.name,
    share.workspace.identifier
  )

  const {
    unpublishShare,
    updatePublicPermissions,
    permissions,
  } = usePublicPermissions({
    share,
    workspaceIdentifier: share.workspace.identifier,
  })
  const {
    publicInspectEnabled,
    publicCommentsEnabled,
    publicAccessLevel,
  } = permissions

  const handleSetInspectEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    const publicInspectEnabled = e?.target?.checked ?? false
    updatePublicPermissions({ publicInspectEnabled })
  }

  const handleSetCommentsEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    const publicCommentsEnabled = e?.target?.checked ?? false
    updatePublicPermissions({ publicCommentsEnabled })
  }

  const handlePublicDocumentChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleSetLevel(e.target.value as PublicAccessLevel)
  }

  const handleSetLevel = async (level: PublicAccessLevel) => {
    const downgradeToNonePermission =
      publicAccessLevel === 'VIEW' && level === 'NONE'

    if (downgradeToNonePermission && share.publication) {
      openModal('confirm-private-community', () => {
        unpublishShare({ publicAccessLevel: level })
      })
    } else if (downgradeToNonePermission) {
      openModal('confirm-private', () => {
        updatePublicPermissions({ publicAccessLevel: level })
      })
    } else {
      updatePublicPermissions({ publicAccessLevel: level })
    }
  }

  const level =
    (publicAccessLevel as string) === 'INSPECT' ? 'VIEW' : publicAccessLevel

  const isPublic = level === 'VIEW'

  return (
    <>
      <Text textStyle="copy.primary.standard.F">
        Anyone with the link to this document&hellip;
      </Text>
      <RadioButton
        name="publicDoc"
        label="Can view the document"
        checked={isPublic}
        value="VIEW"
        onChange={handlePublicDocumentChange}
      />
      <RadioButton
        name="publicDoc"
        label="Cannot access the document"
        checked={!isPublic}
        value="NONE"
        onChange={handlePublicDocumentChange}
      />
      <Text textStyle="copy.primary.standard.F" pt={6}>
        Anyone with the public link can:
      </Text>
      <Checkbox
        value={publicInspectEnabled ? 1 : 0}
        checked={publicInspectEnabled}
        name="inspectEnabled"
        data-testid="share-inspect"
        label="Inspect and Download"
        disabled={!isPublic}
        onChange={handleSetInspectEnabled}
      />
      <Checkbox
        value={publicCommentsEnabled ? 1 : 0}
        checked={publicCommentsEnabled}
        name="commentsEnabled"
        data-testid="share-comments"
        label="Comment"
        disabled={!isPublic}
        onChange={handleSetCommentsEnabled}
      />
      {modal}
    </>
  )
}
